import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
    Grid,
    TextField,
    Button,
    Autocomplete,
    Stack,
    MenuItem,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    FormGroup,
    FormControlLabel,
    Checkbox,
    IconButton,
    InputAdornment
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import configServ from '../../services/config';
import toast from 'react-hot-toast';
import VisibilityIcon from "@mui/icons-material/Visibility";
import Swal from "sweetalert2";
import AddCustomerDialog from "./AddCustomerDialog";
import FirDialog from "./FirDialog";
import { createFilterOptions } from "@mui/material/Autocomplete";

const filter = createFilterOptions();

const initialFormState = {
    client_id: [], // Array for multi-selection
    case_no: '',
    case_category_id: '', // Mapped to Category
    case_type_id: '', // Mapped to Brand
    court_name: '',
    judge_name: '',
    filing_date: new Date().toISOString().split('T')[0], // Default to today's date in YYYY-MM-DD format
    opposite_party: '',
    stage_id: '', // Mapped to Group
    priority: 'Medium',
    status: 'Active',
    description: '',
    case_title: '',
};

const courtOptions = [
    "Supreme Court",
    "High Court",
    "District Court",
    "Civil Court",
    "Sessions Court",
    "Family Court",
    "Consumer Court",
    "Labour Court",
    "Commercial Court",
    "Small Causes Court",
    "Juvenile Court",
    "Motor Accident Claims Tribunal (MACT)",
    "Debt Recovery Tribunal (DRT)",
    "National Company Law Tribunal (NCLT)",
    "Central Administrative Tribunal (CAT)",
    "Revenue Court",
    "Gram Nyayalaya",
    "Special CBI Court",
    "Fast Track Court",
    "Lok Adalat",
    "Armed Forces Tribunal (AFT)",
    "Railway Claims Tribunal",
    "Electricity Tribunal",
    "Income Tax Appellate Tribunal (ITAT)",
    "Customs, Excise & Service Tax Appellate Tribunal (CESTAT)",
    "National Green Tribunal (NGT)",
    "Special POCSO Court",
    "Special NDPS Court",
    "Special CBI/ACB Court",
];

const CaseCard = ({
    customersList = [],
    company_id,
    isCreate,
    setIsCreate,
    isEdit,
    setIsEdit,
    editData,
    setEditData,
    onSeccess,
    categoryList = [],
    brandList = [],
    groupList = [],
    onCategoryRefresh,
    customerId,
    setCustomerId,
    fetchCustomerList,
    user_id,
}) => {
    const [formData, setFormData] = useState(initialFormState);
    const [selectedCustomers, setSelectedCustomers] = useState([]); // Array state for multi-select
    const [openDialog, setOpenDialog] = useState(false); // State for FIR Details Dialog
    const [selectedFir, setSelectedFir] = useState(null); // State for selected FIR

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [searchedName, setSearchedName] = useState("");

    // Validation errors state
    const [errors, setErrors] = useState({});
    // console.log("categoryList", categoryList)

    // Real-time input handler + dynamic error removal
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));

        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    // Form validation logic
    const validateForm = () => {
        let tempErrors = {};

        // 1. Client ID validation (Check if array is empty)
        if (!formData.client_id || formData.client_id.length === 0) {
            tempErrors.client_id = "At least one client selection is required.";
        }

        // 2. Case Number validation
        if (!formData.case_no.trim()) {
            tempErrors.case_no = "Case number is required.";
        } else if (formData.case_no.length > 100) {
            tempErrors.case_no = "Case number cannot exceed 100 characters.";
        }

        // 3. Case ID (Category) validation
        const selectedCaseType = brandList.find(
            (item) => item.id === formData.case_type_id
        );
        // console.log(selectedCaseType);

        // Case validation only when isPrimary = 1
        if (selectedCaseType?.isPrimary == 1 && !formData.case_category_id) {
            tempErrors.case_category_id = "Case selection is required.";
        }

        // 4. Case Type ID (Brand) validation
        if (!formData.case_type_id) {
            tempErrors.case_type_id = "Case type selection is required.";
        }

        if (!formData.opposite_party.trim()) {
            tempErrors.opposite_party = "Opposite party is required.";
        }

        if (!formData.stage_id) {
            tempErrors.stage_id = "Stage selection is required.";
        }

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    useEffect(() => {
        if (isEdit && editData) {

            const formattedDate = editData.filing_date
                ? new Date(editData.filing_date).toISOString().split("T")[0]
                : "";

            setFormData({
                ...editData,
                client_id: editData.client_ids || [],
                category_id: editData.categoryid?.toString() || "",
                case_type_id: editData.case_type_id?.toString() || "",
                stage_id: editData.stage_id?.toString() || "",
                filing_date: formattedDate,
            });

            // Customer Multi Select
            if (Array.isArray(editData.client_ids)) {
                const clients = customersList.filter((item) =>
                    editData.client_ids.includes(item.id)
                );

                setSelectedCustomers(clients);
            } else {
                setSelectedCustomers([]);
            }

            // console.log(editData);
        }
    }, [isEdit, editData, customersList]);

    // Handle save action
    const handleSave = async () => {
        if (!validateForm()) {
            toast.error("Please fill all required fields.");
            return;
        }

        try {
            const payload = {
                ...formData,
                company_id,
            };

            let response;

            if (isEdit) {
                response = await configServ.updateCase(editData.id, payload);
            } else {
                response = await configServ.createCase(payload);
            }

            if (
                response?.status === 200 ||
                response?.status === 201 ||
                response?.status === true
            ) {
                toast.success(
                    response?.data?.message ||
                    (isEdit ? "Case updated successfully." : "Case created successfully.")
                );

                handleReset();
                setIsCreate(false);
                onSeccess();

                if (!isEdit) {
                    await Swal.fire({
                        title: "Case Created Successfully!",
                        text: "Please review and adjust the Previous Hearing Date and Next Hearing Date after adding the hearing details.",
                        icon: "info",
                        confirmButtonText: "OK",
                        confirmButtonColor: "#1976d2",
                    });
                }
            } else {
                toast.error(
                    response?.data?.message ||
                    (isEdit ? "Failed to update case." : "Failed to create case.")
                );
            }
        } catch (error) {
            const errors = error?.response?.data?.errors;

            if (errors) {
                Object.values(errors).forEach((err) => {
                    toast.error(err[0]);
                });
            } else {
                toast.error(error?.response?.data?.message || "Something went wrong.");
            }
        }
    };

    // Handle form reset
    const handleReset = () => {
        setFormData(initialFormState);
        setSelectedCustomers([]);
        setErrors({});
        setIsEdit(false);
        setEditData(null);
        setCustomerId(null); // Reset customerId when resetting the form
        setSelectedFir(null);
    };

    const handleSaveFir = async (data) => {
        try {
            const dataToSend = {
                ...data,
                company_id,
            };

            // console.log(dataToSend);

            const addRes = await configServ.addCategory(dataToSend);

            if (addRes.status === 200) {
                const categoryList = await onCategoryRefresh();

                // console.log("Newly added FIR categoryid:", addRes.data.categoryid);
                // console.log("Newly added FIR categoryid:", categoryList);

                setFormData((prev) => ({
                    ...prev,
                    case_category_id: addRes.data.categoryid, // Set the newly added FIR's categoryid
                }));
                setOpenDialog(false);
            } else {
                toast.error(addRes.message || "Failed to add FIR details.");
                return;
            }
        } catch (error) {
            console.error(error);
            // toast.error("Something went wrong");
        }
    };



    useEffect(() => {
        // console.log("customerId changed:", customerId, customersList);
        if (customerId && customersList.length > 0) {
            const customer = customersList.find(
                (item) => item.id === customerId
            );

            if (customer) {
                setSelectedCustomers((prev) => {
                    // agar already selected hai toh dobara mat jodo
                    if (prev.some((c) => c.id === customer.id)) return prev;
                    return [...prev, customer];
                });

                setFormData((prev) => ({
                    ...prev,
                    client_id: prev.client_id.includes(customer.id)
                        ? prev.client_id
                        : [...prev.client_id, customer.id],
                }));
            }
        }
    }, [customerId, customersList]); // 👈 customersList hataya dependency se

    const handleViewFir = () => {
        const fir = categoryList.find(
            (item) => item.categoryid === formData.case_category_id
        );
        // // console.log("Viewing FIR:", fir);

        if (!fir) return;

        Swal.fire({
            title: `FIR No. ${fir.fir_no}`,
            html: `
            <div style="text-align:left">
                <p><b>FIR No:</b> ${fir.fir_no ?? "-"}</p>
                <p><b>Filing Date:</b> ${fir.filing_date ?? "-"}</p>
                <p><b>FIR SUMMARY:</b> ${fir.categoryname ?? "-"}</p>
            </div>
        `,
            icon: "info",
            confirmButtonText: "Close",
        });
    };

    const handleCustomerCreated = async (newCustomer) => {
        try {
            const dataToSend = {
                ...newCustomer,
                company_id,
                user_id,
            };

            const res = await configServ.addCustomer(dataToSend);

            if (res.status === 200) {
                toast.success(res.message);

                const customers = await fetchCustomerList();

                const newCustomerId = res.data.id;

                const createdCustomer = customers.find(
                    (item) => item.id === newCustomerId
                );
                // // console.log(createdCustomer);

                if (createdCustomer) {
                    setSelectedCustomers((prev) => [...prev, createdCustomer]);

                    setFormData((prev) => ({
                        ...prev,
                        client_id: [...prev.client_id, createdCustomer.id],
                    }));
                } else {
                    // // console.warn("Created customer not found in list:", newCustomerId, customers);
                    toast.error("Customer created but not found in list. Please refresh.");
                }

                setIsDialogOpen(false);
            }
        } catch (e) {
            // console.error("handleCustomerCreated error:", e);
            toast.error("Something went wrong while adding customer.");
        }
    };



    return (

        <>


            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -20 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                style={{
                    width: "100%",
                    backgroundColor: "#e3f3fc",
                    padding: "20px",
                    borderRadius: "10px",
                    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
                }}
            >
                <Grid container spacing={2} sx={{ width: "100%", marginBottom: 2 }}>

                    {/* Multi-Select Customer Dropdown */}
                    <Grid item xs={12} sm={4}>
                        <Autocomplete
                            multiple
                            value={selectedCustomers}
                            options={customersList}
                            getOptionLabel={(option) => {
                                if (option.id === "__add__") return option.fname;
                                return option.father_name
                                    ? `${option.fname} (${option.father_name})`
                                    : option.fname;
                            }}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            filterOptions={(options, params) => {
                                const filtered = options.filter((option) =>
                                    option.fname?.toLowerCase().includes(params.inputValue.toLowerCase())
                                );

                                const { inputValue } = params;

                                if (filtered.length === 0 && inputValue !== "") {
                                    filtered.push({
                                        id: "__add__",
                                        fname: `➕ Add "${inputValue}" as New Customer`,
                                        searchedText: inputValue,
                                    });
                                }

                                return filtered;
                            }}
                            onChange={(event, newValue) => {
                                const addOption = newValue.find((item) => item.id === "__add__");

                                if (addOption) {
                                    // Typed input set karein aur dialog open karein
                                    setSearchedName(addOption.searchedText);
                                    setIsDialogOpen(true);
                                    return;
                                }

                                setSelectedCustomers(newValue);
                                setFormData((prev) => ({
                                    ...prev,
                                    client_id: newValue.map((item) => item.id),
                                }));
                            }}
                            renderTags={(tagValue, getTagProps) =>
                                tagValue.map((option, index) => {
                                    if (option.id === "__add__") return null;
                                    const { key, ...tagProps } = getTagProps({ index });
                                    return (
                                        <Chip
                                            key={key}
                                            label={option.fname || `Client #${option.id}`}
                                            size="small"
                                            {...tagProps}
                                        />
                                    );
                                })
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Customer(s)"
                                    variant="outlined"
                                    size="small"
                                    error={!!errors.client_id}
                                    helperText={errors.client_id}
                                    sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
                                />
                            )}
                        />
                    </Grid>

                    {/* Case Type ID Selection (Mapped to Brand) */}
                    <Grid item xs={12} sm={4}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Case Type"
                            name="case_type_id"
                            value={formData.case_type_id}
                            onChange={handleChange}
                            error={!!errors.case_type_id}
                            helperText={errors.case_type_id}
                            sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
                        >
                            {brandList.map((brand) => (
                                <MenuItem key={brand.id} value={brand.id}>
                                    {brand.brand_name || brand.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>


                    {/* Case ID Selection (Mapped to Category) */}
                    <Grid item xs={12} sm={4}>

                        <Autocomplete
                            options={categoryList}
                            value={
                                categoryList.find(
                                    (item) => item.categoryid === formData.case_category_id
                                ) || null
                            }
                            getOptionLabel={(option) =>
                                ` ${option.fir_no ?? ""}`
                            }
                            // ${option.categoryname}
                            filterOptions={(options, { inputValue }) => {
                                const filtered = options.filter((option) =>
                                    (option.fir_no ?? "")
                                        .toLowerCase()
                                        .includes(inputValue.toLowerCase())
                                );

                                if (filtered.length === 0 && inputValue !== "") {
                                    filtered.push({
                                        categoryid: "__add__",
                                        fir_no: `➕ Add "${inputValue}" as New FIR No.`,
                                        searchedText: inputValue, // 👈 raw typed value yahan store
                                    });
                                }

                                return filtered;
                            }}
                            onChange={(event, value) => {
                                if (!value) return;

                                if (value.categoryid === "__add__") {
                                    setSelectedFir(value.searchedText); // 👈 ab sirf typed text jayega
                                    setOpenDialog(true); // popup open
                                    return;
                                }

                                setFormData((prev) => ({
                                    ...prev,
                                    case_category_id: value.categoryid,
                                }));
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="FIR No."
                                    size="small"
                                    error={!!errors.case_category_id}
                                    helperText={errors.case_category_id}
                                    sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {formData.case_category_id && (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            size="small"
                                                            onClick={handleViewFir}
                                                        >
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    </InputAdornment>
                                                )}

                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    {/* Case Number */}
                    <Grid item xs={12} sm={4}>
                        <TextField
                            name="case_no"
                            label="Case Number"
                            variant="outlined"
                            value={formData.case_no}
                            onChange={handleChange}
                            error={!!errors.case_no}
                            helperText={errors.case_no}
                            fullWidth
                            size="small"
                            sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <TextField
                            name="case_title"
                            label="Case Title"
                            variant="outlined"
                            value={formData.case_title}
                            onChange={handleChange}
                            // error={!!errors.case_no}
                            // helperText={errors.case_no}
                            fullWidth
                            size="small"
                            sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
                        />
                    </Grid>

                    {/* Opposite Party */}
                    <Grid item xs={12} sm={4}>
                        <TextField
                            name="opposite_party"
                            label="Opposite Party"
                            variant="outlined"
                            value={formData.opposite_party}
                            error={!!errors.opposite_party}
                            helperText={errors.opposite_party}
                            onChange={handleChange}
                            fullWidth
                            size="small"
                            sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
                        />
                    </Grid>




                    {/* Court Name */}
                    <Grid item xs={12} sm={4}>
                        <Autocomplete
                            options={courtOptions}
                            value={formData.court_name || null}
                            onChange={(event, newValue) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    court_name: newValue || "",
                                }));
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Court Name"
                                    size="small"
                                    fullWidth
                                    sx={{ backgroundColor: "#fff", borderRadius: "4px" }}

                                />
                            )}
                        />
                    </Grid>

                    {/* Judge Name */}
                    {/* <Grid item xs={12} sm={4}>
                        <TextField
                            name="judge_name"
                            label="Judge Name"
                            variant="outlined"
                            value={formData.judge_name}
                            onChange={handleChange}
                            fullWidth
                            size="small"
                            sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
                        />
                    </Grid> */}

                    {/* Filing Date */}
                    <Grid item xs={12} sm={4}>
                        <TextField
                            name="filing_date"
                            label="Filing Date"
                            type="date"
                            variant="outlined"
                            value={formData.filing_date}
                            onChange={handleChange}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
                        />
                    </Grid>



                    {/* Stage ID Selection (Mapped to Group) */}
                    <Grid item xs={12} sm={4}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Stage"
                            name="stage_id"
                            value={formData.stage_id}
                            onChange={handleChange}
                            sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
                            error={!!errors.stage_id}
                            helperText={errors.stage_id}
                        >
                            {groupList.map((group) => (
                                <MenuItem key={group.id} value={group.id}>
                                    {group.group_name || group.title}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    {/* Priority */}
                    {/* <Grid item xs={12} sm={4}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Priority"
                            name="priority"
                            value={formData.priority}
                            onChange={handleChange}
                            sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
                        >
                            <MenuItem value="Low">Low</MenuItem>
                            <MenuItem value="Medium">Medium</MenuItem>
                            <MenuItem value="High">High</MenuItem>
                            <MenuItem value="Urgent">Urgent</MenuItem>
                        </TextField>
                    </Grid> */}

                    {/* Status */}
                    <Grid item xs={12} sm={4}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Status"
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                            sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
                        >
                            <MenuItem value="Active">Active</MenuItem>
                            <MenuItem value="Pending">Pending</MenuItem>
                            <MenuItem value="Disposed">Disposed</MenuItem>
                            <MenuItem value="Closed">Closed</MenuItem>
                            <MenuItem value="On Hold">On Hold</MenuItem>
                        </TextField>
                    </Grid>



                    {/* Description */}
                    <Grid item xs={12} sm={4}>
                        <TextField
                            name="description"
                            label="Description"
                            variant="outlined"
                            value={formData.description}
                            onChange={handleChange}
                            fullWidth
                            multiline
                            rows={3}
                            size="small"
                            sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
                        />
                    </Grid>
                </Grid>

                {/* Form Action Controls */}
                <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                        width: "100%",
                        justifyContent: "flex-end",
                        marginTop: 2,
                    }}
                >
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<RotateLeftIcon />}
                        onClick={handleReset}
                        sx={{
                            borderRadius: 2,
                            px: 3,
                            py: 1,
                            backgroundColor: "secondary.main",
                            "&:hover": { backgroundColor: "secondary.dark" },
                        }}
                    >
                        Reset
                    </Button>

                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        sx={{
                            borderRadius: 2,
                            px: 3,
                            py: 1,
                            backgroundColor: "primary.main",
                            "&:hover": { backgroundColor: "primary.dark" },
                        }}
                    >
                        {isEdit ? "Update" : "Save"}
                    </Button>
                </Stack>
            </motion.div>
            <FirDialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                onSave={handleSaveFir}
                selectedFir={selectedFir}
            />
            <AddCustomerDialog
                isOpen={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                initialName={searchedName}
                onCustomerCreated={handleCustomerCreated}
            />
        </>


    );
};

export default CaseCard;